import React, { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import storage from '@dashboard/services/storage';

import AuthLayout from '@dashboard/layouts/AuthLayout';

import Form from '@dashboard/components/Formik/Form';
import Meta from '@dashboard/components/Meta';
import { GoogleSignIn } from '@dashboard/components/GoogleSignIn';
import {
  AuthDivider,
  AuthErrorLink,
  AuthFieldError,
  AuthFooter,
  AuthHeader,
  AuthLink,
  authButtonClassName,
  authRedirectPath,
  authInputClassName,
  invalidFieldPropsAreEqual,
  renderAuthField,
  WatchValues,
} from '@dashboard/components/AuthForm';
import { appUrl } from '@/dashboard/services/env';

function ForgotPasswordLink() {
  const { values } = useFormikContext();

  return (
    <AuthLink path="/account/forgot-password" email={values.email}>
      Forgot password?
    </AuthLink>
  );
}

export default function Login() {
  const { login, setLogged } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const prefilledEmail = location.state?.email || '';
  const [email, setEmail] = useState(prefilledEmail);
  const [fieldError, setFieldError] = useState(null);

  const handleValues = useCallback(values => {
    setEmail(values.email);
    setFieldError(null);
  }, []);

  return (
    <>
      <Meta
        meta={{
          title: 'Log in',
        }}
      >
        <link rel="canonical" href={`${appUrl}/login`} />
      </Meta>

      <AuthLayout>
        <AuthHeader title="Log in to Checkout Page" />

        <GoogleSignIn mode="login" />

        <AuthDivider />

        <Form
          initialValues={{
            email: prefilledEmail,
            password: '',
          }}
          onSubmit={async values => {
            const response = await login({
              ...values,
              session: storage.local.getItem('CheckoutPage.sessionId'),
            });

            if (response.status === 404) {
              setFieldError({ field: 'email', email: values.email });
              return { data: null, error: '' };
            }
            if (response.status === 403 && response.error === 'Incorrect password') {
              setFieldError({ field: 'password' });
              return { data: null, error: '' };
            }

            return {
              data: response.data,
              error: response.error || response.message,
              onSuccess: () => {
                setLogged({
                  user: response.data.user,
                  seller: response.data.user.currentSeller,
                  token: response.data.token,
                });

                history.push(authRedirectPath(location.search));
              },
            };
          }}
          showState={false}
          customFieldPropsAreEqual={invalidFieldPropsAreEqual}
          inputClassName={authInputClassName}
          renderField={renderAuthField}
          fields={[
            {
              name: 'email',
              required: true,
              type: 'email',
              label: 'Email',
              placeholder: 'name@example.com',
              autoComplete: 'username',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
              'aria-invalid': fieldError?.field === 'email' ? true : undefined,
              additionalContent: fieldError?.field === 'email' && (
                <AuthFieldError>
                  No account uses this email.{' '}
                  <AuthErrorLink path="/signup" email={fieldError.email}>
                    Create a free account
                  </AuthErrorLink>
                </AuthFieldError>
              ),
            },
            {
              name: 'password',
              required: true,
              type: 'password',
              label: 'Password',
              autoComplete: 'current-password',
              autoFocus: !!prefilledEmail,
              'aria-invalid': fieldError?.field === 'password' ? true : undefined,
              additionalContent: fieldError?.field === 'password' && (
                <AuthFieldError>Incorrect password.</AuthFieldError>
              ),
            },
          ]}
          submitButton={{
            label: 'Log in',
            variant: 'primary',
            size: 'full',
            className: authButtonClassName,
          }}
        >
          <WatchValues onChange={handleValues} />
          <div className="-mt-1 flex justify-end text-sm">
            <ForgotPasswordLink />
          </div>
        </Form>

        <AuthFooter>
          New to Checkout Page?{' '}
          <AuthLink path="/signup" email={email}>
            Create a free account
          </AuthLink>
        </AuthFooter>
      </AuthLayout>
    </>
  );
}
